import React, { useState, useEffect } from "react";
import { Users } from "lucide-react";
import ImageSlider from "../components/ImageSlider";

const Home = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBlogs: 0,
    leaderboard: [],
    isLoading: true,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/stats`
        );
        const data = await response.json();

        setStats({
          totalUsers: data.totalUsers,
          totalBlogs: data.totalBlogs,
          leaderboard: data.leaderboard || [], // Assuming leaderboard comes in API response
          isLoading: false,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
        setStats((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchStats();
  }, []);

  // Loading animation component
  const LoadingAnimation = () => (
    <div className="flex space-x-2 justify-center items-center h-12">
      <div
        className="w-2 h-2 bg-green-400 rounded-full animate-bounce"
        style={{ animationDelay: "0s" }}
      ></div>
      <div
        className="w-2 h-2 bg-green-400 rounded-full animate-bounce"
        style={{ animationDelay: "0.2s" }}
      ></div>
      <div
        className="w-2 h-2 bg-green-400 rounded-full animate-bounce"
        style={{ animationDelay: "0.4s" }}
      ></div>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-black overflow-hidden">
      {/* Enhanced Background Animation */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('/svg (1).png')",
          opacity: 0.8,
        }}
      />

      {/* Animated Background Gradients */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-50 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="relative p-8">
        <div className="max-w-4xl mx-auto">
          {/* Enhanced Header */}
          <div className="text-center mb-5">
            {/* Welcome Message */}
            <p className="text-3xl mt-3 mb-6 font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 animate-bounce">
              Welcome to TechHook ...
            </p>
          </div>

          <ImageSlider className="mb-6 "></ImageSlider>
          {/* Welcome Message Card */}

          {/* Stats Cards Container */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Members Card */}
            <div className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg blur opacity-50 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-gradient-x"></div>
              <div className="relative bg-slate-800 rounded-lg p-8">
                {stats.isLoading ? (
                  <LoadingAnimation />
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-300 group-hover:text-white transition-colors">
                        Total Members
                      </h3>
                      <p className="text-4xl font-bold text-green-400 mt-2 group-hover:scale-110 transform transition-transform">
                        {stats.totalUsers.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-green-400 group-hover:rotate-12 transform transition-transform">
                      <Users size={48} strokeWidth={1.5} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Blogs Card */}
            <div className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg blur opacity-50 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-gradient-x"></div>
              <div className="relative bg-slate-800 rounded-lg p-8">
                {stats.isLoading ? (
                  <LoadingAnimation />
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-300 group-hover:text-white transition-colors">
                        Total Blogs
                      </h3>
                      <p className="text-4xl font-bold text-purple-400 mt-2 group-hover:scale-110 transform transition-transform">
                        {stats.totalBlogs.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-5xl text-purple-400 group-hover:rotate-12 transform transition-transform">
                      📚
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Leaderboard Card */}
          <div className="group relative p-1 mx-auto w-full max-w-4xl">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg blur opacity-50 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-gradient-x"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-600 rounded-lg blur-lg opacity-75 group-hover:opacity-100 transition-opacity"></div>

            <div className="relative min-h-96 bg-slate-900 p-4 sm:p-6 lg:p-8 rounded-lg transform transition-all duration-300 ease-in-out border border-green-400 shadow-[0_0_15px_rgba(34,197,94,0.5)]">
              <h3 className="text-xl sm:text-2xl font-bold text-green-400 text-center mb-4 sm:mb-6 tracking-wide drop-shadow-[0_0_10px_rgba(34,197,94,0.8)]">
                🏆 Leaderboard 🏆
              </h3>

              <div className="space-y-4 sm:space-y-6">
                {stats.isLoading ? (
                  <LoadingAnimation />
                ) : (
                  stats.leaderboard.map((user, index) => (
                    <div
                      key={user.id}
                      className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 sm:p-4 rounded-lg shadow-[0_0_10px_rgba(34,197,94,0.3)] gap-2 sm:gap-0
                  ${
                    index === 0
                      ? "bg-green-900/50 border-2 border-green-400"
                      : index === 1
                      ? "bg-green-900/40 border border-green-500"
                      : index === 2
                      ? "bg-green-900/30 border border-green-600"
                      : "bg-slate-800/50"
                  }`}
                    >
                      <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
                        <span
                          className={`text-base sm:text-lg font-bold drop-shadow-[0_0_8px_rgba(34,197,94,0.8)] ${
                            index === 0
                              ? "text-green-300"
                              : index === 1
                              ? "text-green-400"
                              : index === 2
                              ? "text-green-500"
                              : "text-green-600"
                          }`}
                        >
                          #{index + 1}{" "}
                          {index === 0
                            ? "🥇"
                            : index === 1
                            ? "🥈"
                            : index === 2
                            ? "🥉"
                            : ""}
                        </span>
                        <p className="text-base sm:text-lg font-medium text-green-300 truncate max-w-[150px] sm:max-w-none">
                          {user.username}
                        </p>
                      </div>

                      <div className="flex items-center space-x-3 sm:space-x-6 w-full sm:w-auto justify-start sm:justify-end">
                        <p className="text-xs sm:text-sm text-green-400">
                          Posts:{" "}
                          <span className="text-green-300 font-semibold drop-shadow-[0_0_5px_rgba(34,197,94,0.6)]">
                            {user.totalPosts}
                          </span>
                        </p>
                        <p className="text-xs sm:text-sm text-green-400 flex items-center">
                          Reactions: ❤️
                          <span className="text-green-300 flex items-center ml-1 font-semibold">
                            {user.totalReactions}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add custom styles for animations */}
        <style jsx>{`
          @keyframes blob {
            0% {
              transform: translate(0px, 0px) scale(1);
            }
            33% {
              transform: translate(30px, -50px) scale(1.1);
            }
            66% {
              transform: translate(-20px, 20px) scale(0.9);
            }
            100% {
              transform: translate(0px, 0px) scale(1);
            }
          }
          .animate-blob {
            animation: blob 7s infinite;
          }
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          .animation-delay-4000 {
            animation-delay: 4s;
          }
          .animate-gradient {
            background-size: 200% 200%;
            animation: gradient 15s ease infinite;
          }
          @keyframes gradient {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default Home;
